import { PricingRangeWithDiscountProps } from '../types';
import { orderPricingRange } from './orderPricingRange';

/**
 * Finds the applicable price range for a given number of employees
 *
 * @param pricingRange - Array of price ranges with different employee thresholds
 * @param nbEmployee - Number of employees to find pricing for
 *
 * @returns The matching price range object or null if:
 * - No price range matches the employee count
 * - The matching price range is marked as 'onDemand'
 *
 * The function:
 * 1. Orders price ranges by employee threshold (from)
 * 2. Reverses the order to find the highest applicable threshold
 * 3. Returns the first range where employee count >= threshold
 *
 * @example
 * ```ts
 * const ranges = [
 *   { from: 0, price: 50, base: 100 },
 *   { from: 10, price: 40, base: 200 },
 *   { from: 50, price: 30, base: 500 }
 * ];
 *
 * getPrice(ranges, 25);
 * // Returns: { from: 10, price: 40, base: 200 }
 *
 * getPrice(ranges, 5);
 * // Returns: { from: 0, price: 50, base: 100 }
 * ```
 */
export default function getPrice<T extends PricingRangeWithDiscountProps>(
  pricingRange: T[],
  nbEmployee: number,
): T | null {
  const pricingRangeOrdered = orderPricingRange(pricingRange).reverse();
  const price = pricingRangeOrdered.find((price) => {
    return price.from <= nbEmployee;
  });

  if (price?.onDemand) return null;

  return price || null;
}
